import React, { useState, useEffect } from 'react';
import {
  IonIcon,
  IonButton,
} from '@ionic/react';
import { addCircle, sparkles } from 'ionicons/icons';
import {
  Chip,
  Divider,
  IconButton,
  Typography,
  MenuItem,
  Dialog,
  DialogTitle,
  Stack,
} from '@mui/material';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import Linkify from 'linkify-react';
import { LightTextField } from 'components/StyledInput';
import './Goals.css';
import appStrings from 'common/app_strings';
import { displayName } from 'helpers/textManipulation';
import appColors from 'common/app_colors';
import PostIndicator from 'components/PostIndicator';

const { goals: strings } = appStrings;
const {
  text, buttons, container, icon,
} = appColors;

const updatesChipStyles = {
  fontWeight: 700,
  fontSize: '14px',
  backgroundColor: container.beige,
  color: text.blackText,
  border: `2px solid ${text.blackText}`,
};

const noUpdatesChipStyles = {
  backgroundColor: buttons.lightGrey,
  border: 'none',
  color: text.lightBlack,
};

const Goal = ({
  goal,
  handleEdit,
  handleStatus,
  handleVisibility,
  handleDelete,
  goToRelatedPost,
}) => {
  const [open, setOpen] = useState(false);
  const [isChangingVisibility, setIsChangingVisibility] = useState(false);
  const { control, handleSubmit, watch } = useForm();
  const legacyGoals = ['academic', 'passion', 'career', 'health'];

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const deleteGoal = (userGoal) => {
    handleDelete(userGoal);
    handleClose();
  };

  const changeVisibility = async (userGoal, visibility) => {
    setIsChangingVisibility(true);
    await handleVisibility(userGoal, visibility);
    setIsChangingVisibility(false);
  };

  const onSubmit = async (data) => {
    handleStatus(goal, data);
  };

  useEffect(() => {
    const subscription = watch(handleSubmit(onSubmit));
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch]);

  return (
    <>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle textAlign="center" px={8}>
          {strings.confirmDelete}
        </DialogTitle>
        <Stack spacing={2} mb={2} px={4}>
          <IonButton
            style={{ '--background': buttons.deleteRedButton, '--color': text.whiteText }}
            size="large"
            expand="block"
            shape="round"
            onClick={() => deleteGoal(goal)}
          >
            {strings.yesDelete}
          </IonButton>
          <IonButton size="large" expand="block" shape="round" fill="outline" onClick={handleClose}>
            {strings.noDelete}
          </IonButton>
        </Stack>
      </Dialog>
      <div className="goals-item">
        <div className="goals-item-description">
          <div className={`goals-type-wrapper goals-indicator-${goal?.type}`}>
            <img
              style={legacyGoals.includes(goal?.type) ? { marginRight: '4px' } : undefined}
              className={`goals-type-icon ${goal?.type}-icon`}
              src={!legacyGoals.includes(goal?.type) ? `/assets/goals/${goal?.type}.svg` : '/assets/goals/bullseye_outline.svg'}
              alt={`${goal?.type} goal icon`}
            />
          </div>
          <div style={{ width: '100%' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: text.darkNavy,
                  }}
                >
                  {displayName(goal?.user?.first_name, goal?.user?.last_name)}
                </Typography>
                <span className="post-time">
                  {formatDistanceToNowStrict(parseISO(goal?.created_at), {
                    addSuffix: true,
                  })}
                </span>
              </div>
              <div>
                <PostIndicator text={`${goal?.type} ${strings.labels.goal}`} type={goal?.type} />
              </div>
            </div>
            <Typography className="goals-text" color={text.lightBlack}>
              <Linkify>{goal?.content}</Linkify>
            </Typography>
          </div>
        </div>
        <div className="goals-item-status">
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '65%' }}>
            <Controller
              control={control}
              name="status"
              defaultValue={goal?.status || 'in_progress'}
              render={({ field }) => (
                <LightTextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...field}
                  select
                  size="small"
                  fullWidth
                >
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </LightTextField>
              )}
            />
          </form>
          {goal?.post?.id && (
            <Chip
              label={strings.seeUpdates(goal?.updatesCount)}
              sx={{
                ...updatesChipStyles,
                ...(goal?.updatesCount === 0 && noUpdatesChipStyles),
              }}
              onClick={() => goToRelatedPost(goal?.post?.id)}
            />
          )}
        </div>
        <div className="goals-item-options">
          <div>
            <IconButton size="small" sx={{ pl: 0 }} onClick={() => handleEdit(goal)}>
              <IonIcon
                style={{ fontSize: '24px' }}
                color="warning"
                slot="icon-only"
                src="/assets/icon/edit.svg"
              />
            </IconButton>
            <IconButton
              size="small"
              disabled={isChangingVisibility}
              onClick={() => (goal?.visibility === 'team'
                ? changeVisibility(goal, 'public')
                : changeVisibility(goal, 'team'))}
            >
              <IonIcon
                style={{
                  fontSize: '24px',
                  stroke: 'var(--ion-color-warning)',
                }}
                color="warning"
                slot="icon-only"
                src={
                  goal?.visibility === 'public'
                    ? '/assets/icon/eye.svg'
                    : '/assets/icon/eye-closed.svg'
                }
              />
            </IconButton>
            <IconButton size="small" onClick={handleOpen}>
              <IonIcon
                style={{ fontSize: '24px' }}
                color="warning"
                slot="icon-only"
                src="/assets/icon/trash.svg"
              />
            </IconButton>
            {goal?.post?.id && (
              <IconButton size="small" onClick={() => goToRelatedPost(goal?.post?.id)}>
                <IonIcon
                  style={{ fontSize: '24px' }}
                  color="warning"
                  slot="icon-only"
                  icon={addCircle}
                />
              </IconButton>
            )}
          </div>
          {goal?.status === 'completed' && (
            <div>
              <Typography
                variant="span"
                sx={{
                  color: text.greenSuccess,
                  fontSize: 14,
                  fontWeight: 700,
                  mr: 0.75,
                }}
              >
                {strings.achieved}
              </Typography>
              <IonIcon style={{ color: icon.greenSuccess }} slot="icon-only" icon={sparkles} />
            </div>
          )}
        </div>
      </div>
      <Divider sx={{ mt: 2 }} />
    </>
  );
};

Goal.propTypes = {
  goal: PropTypes.shape({
    id: PropTypes.number.isRequired,
    content: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    visibility: PropTypes.string.isRequired,
    post: PropTypes.oneOfType([PropTypes.object]),
  }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleStatus: PropTypes.func.isRequired,
  handleVisibility: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  goToRelatedPost: PropTypes.func.isRequired,
};

export default Goal;
